import { useCallback, useEffect, useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, RefreshCw, Filter } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

function Field({ label, children }) {
    return (
        <label className="flex flex-col gap-2">
            <span
                className="mono text-[10px] uppercase tracking-[0.2em]"
                style={{ color: "var(--text-faint)" }}
            >
                {label}
            </span>
            {children}
        </label>
    );
}

function RuleForm({ initial, channels, onSubmit, onCancel, submitting }) {
    const [name, setName] = useState(initial?.name || "");
    const [eventType, setEventType] = useState(initial?.event_type || "AlertIncident");
    const [payloadSource, setPayloadSource] = useState(initial?.payload_source || "*");
    const [alertType, setAlertType] = useState(initial?.alert_type || "*");
    const [triggerId, setTriggerId] = useState(
        initial?.trigger_id != null ? String(initial.trigger_id) : "",
    );
    const [channelId, setChannelId] = useState(
        initial?.channel_id || channels[0]?.id || "",
    );
    const [format, setFormat] = useState(initial?.format || "both");
    const [enabled, setEnabled] = useState(initial?.enabled ?? true);
    const [priority, setPriority] = useState(
        initial?.priority != null ? String(initial.priority) : "100",
    );

    const submit = (e) => {
        e.preventDefault();
        if (!name || !channelId) {
            toast.error("Name and channel are required");
            return;
        }
        const payload = {
            name,
            event_type: eventType || "*",
            payload_source: payloadSource || "*",
            alert_type: alertType || "*",
            trigger_id: triggerId === "" ? null : Number(triggerId),
            channel_id: channelId,
            format,
            enabled,
            priority: Number(priority) || 100,
        };
        onSubmit(payload);
    };

    return (
        <form onSubmit={submit} className="flex flex-col gap-4">
            <Field label="rule name">
                <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="console-input"
                    data-testid="rule-name-input"
                />
            </Field>
            <div className="grid grid-cols-2 gap-3">
                <Field label="event type (or *)">
                    <input
                        value={eventType}
                        onChange={(e) => setEventType(e.target.value)}
                        className="console-input"
                        data-testid="rule-eventtype-input"
                    />
                </Field>
                <Field label="payload source (or *)">
                    <input
                        value={payloadSource}
                        onChange={(e) => setPayloadSource(e.target.value)}
                        className="console-input"
                        data-testid="rule-source-input"
                    />
                </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <Field label="trigger id (optional)">
                    <input
                        value={triggerId}
                        onChange={(e) => setTriggerId(e.target.value)}
                        className="console-input"
                        data-testid="rule-trigger-input"
                        placeholder="leave blank for any"
                    />
                </Field>
                <Field label="priority">
                    <input
                        value={priority}
                        onChange={(e) => setPriority(e.target.value)}
                        className="console-input"
                        data-testid="rule-priority-input"
                    />
                </Field>
            </div>
            <Field label="alert type (email-ingested only · `*` for any)">
                <input
                    value={alertType}
                    onChange={(e) => setAlertType(e.target.value)}
                    className="console-input"
                    data-testid="rule-alerttype-input"
                    placeholder="e.g. Mobile Usage, Drowsiness, Crash, *"
                />
            </Field>
            <Field label="channel">
                <Select value={channelId} onValueChange={setChannelId}>
                    <SelectTrigger
                        className="console-input"
                        data-testid="rule-channel-select"
                    >
                        <SelectValue placeholder="pick a channel" />
                    </SelectTrigger>
                    <SelectContent
                        className="mono"
                        style={{
                            background: "var(--bg-elev)",
                            border: "1px solid var(--border-strong)",
                            color: "var(--text)",
                        }}
                    >
                        {channels.map((c) => (
                            <SelectItem
                                key={c.id}
                                value={c.id}
                                data-testid={`rule-channel-option-${c.id}`}
                            >
                                {c.name} [{c.type || "glip_webhook"}]{" "}
                                {c.enabled ? "" : "(disabled)"}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </Field>
            <Field label="format">
                <Select value={format} onValueChange={setFormat}>
                    <SelectTrigger
                        className="console-input"
                        data-testid="rule-format-select"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent
                        className="mono"
                        style={{
                            background: "var(--bg-elev)",
                            border: "1px solid var(--border-strong)",
                            color: "var(--text)",
                        }}
                    >
                        <SelectItem value="both">both (text + card)</SelectItem>
                        <SelectItem value="text">text only</SelectItem>
                        <SelectItem value="card">card only</SelectItem>
                    </SelectContent>
                </Select>
            </Field>
            <div className="flex items-center justify-between">
                <span
                    className="mono text-[10px] uppercase tracking-[0.2em]"
                    style={{ color: "var(--text-faint)" }}
                >
                    enabled
                </span>
                <Switch
                    checked={enabled}
                    onCheckedChange={setEnabled}
                    data-testid="rule-enabled-switch"
                />
            </div>
            <DialogFooter className="mt-2 gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="ghost-btn"
                    data-testid="rule-cancel-btn"
                >
                    cancel
                </button>
                <button
                    type="submit"
                    disabled={submitting}
                    className="pill-btn"
                    data-testid="rule-save-btn"
                >
                    {submitting ? "saving…" : initial ? "save" : "create"}
                </button>
            </DialogFooter>
        </form>
    );
}

export default function RulesPage() {
    const [rules, setRules] = useState([]);
    const [channels, setChannels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [rRes, cRes] = await Promise.all([
                api.get("/rules"),
                api.get("/channels"),
            ]);
            setRules(rRes.data.rules || []);
            setChannels(cRes.data.channels || []);
        } catch (e) {
            toast.error("Failed to load rules");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const save = async (payload) => {
        setSaving(true);
        try {
            if (editing?.id) {
                await api.patch(`/rules/${editing.id}`, payload);
                toast.success("Rule updated");
            } else {
                await api.post("/rules", payload);
                toast.success("Rule created");
            }
            setEditing(null);
            await load();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Save failed",
            );
        } finally {
            setSaving(false);
        }
    };

    const toggle = async (r) => {
        try {
            await api.patch(`/rules/${r.id}`, { enabled: !r.enabled });
            await load();
        } catch {
            toast.error("Toggle failed");
        }
    };

    const remove = async () => {
        if (!deleting) return;
        try {
            await api.delete(`/rules/${deleting.id}`);
            toast.success("Rule deleted");
            setDeleting(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
            setDeleting(null);
        }
    };

    return (
        <section className="panel p-6 fade-in" data-testid="rules-panel">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <Filter size={16} style={{ color: "var(--accent)" }} />
                    <h2
                        className="mono text-sm uppercase tracking-[0.18em]"
                        style={{ color: "var(--text)" }}
                    >
                        rules
                    </h2>
                    <span
                        className="mono text-xs"
                        style={{ color: "var(--text-faint)" }}
                    >
                        {rules.length} · sorted by priority
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="ghost-btn"
                        onClick={load}
                        data-testid="rules-refresh-btn"
                    >
                        <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                        refresh
                    </button>
                    <button
                        className="pill-btn"
                        onClick={() =>
                            channels.length === 0
                                ? toast.error(
                                      "Create a channel first before adding a rule",
                                  )
                                : setEditing({})
                        }
                        data-testid="rule-new-btn"
                    >
                        <Plus size={14} /> new rule
                    </button>
                </div>
            </div>

            {rules.length === 0 && !loading && (
                <div
                    className="panel-inset p-10 text-center"
                    data-testid="rules-empty"
                >
                    <div
                        className="mono text-xs uppercase tracking-widest mb-2"
                        style={{ color: "var(--text-faint)" }}
                    >
                        no rules yet
                    </div>
                    <div
                        className="text-sm"
                        style={{ color: "var(--text-dim)" }}
                    >
                        Rules map Samsara events → channels. If none match and no
                        env fallback is set, deliveries land as{" "}
                        <span className="kbd">no_match</span>.
                    </div>
                </div>
            )}

            <div className="flex flex-col gap-2">
                {rules.map((r) => (
                    <div
                        key={r.id}
                        className="panel-inset px-4 py-3 flex items-center gap-3 flex-wrap"
                        data-testid={`rule-row-${r.id}`}
                    >
                        <span
                            className="mono text-[11px]"
                            style={{ color: "var(--text-faint)", width: 36 }}
                            title="priority"
                        >
                            p{r.priority}
                        </span>
                        <span
                            className="mono text-sm"
                            style={{ color: "var(--text)", minWidth: 140 }}
                        >
                            {r.name}
                        </span>
                        <span
                            className="mono text-[11px]"
                            style={{ color: "var(--text-dim)" }}
                        >
                            event={r.event_type} · src={r.payload_source}
                            {r.alert_type && r.alert_type !== "*" && ` · alert=${r.alert_type}`}
                            {r.trigger_id != null && ` · trigger=${r.trigger_id}`}
                        </span>
                        <span
                            className="mono text-[11px] flex-1 truncate"
                            style={{ color: "var(--accent)" }}
                        >
                            → {r.channel_name || r.channel_id}
                        </span>
                        <span
                            className="badge"
                            style={{
                                color: "var(--info)",
                                borderColor: "rgba(125,211,252,0.35)",
                            }}
                        >
                            {r.format}
                        </span>
                        <Switch
                            checked={!!r.enabled}
                            onCheckedChange={() => toggle(r)}
                            data-testid={`rule-toggle-${r.id}`}
                        />
                        <button
                            className="ghost-btn"
                            onClick={() => setEditing(r)}
                            data-testid={`rule-edit-btn-${r.id}`}
                        >
                            <Pencil size={11} /> edit
                        </button>
                        <button
                            className="ghost-btn"
                            onClick={() => setDeleting(r)}
                            data-testid={`rule-delete-btn-${r.id}`}
                            style={{ color: "var(--danger)" }}
                        >
                            <Trash2 size={11} /> delete
                        </button>
                    </div>
                ))}
            </div>

            <Dialog
                open={editing !== null}
                onOpenChange={(o) => !o && setEditing(null)}
            >
                <DialogContent
                    className="mono"
                    style={{
                        background: "var(--bg-elev)",
                        border: "1px solid var(--border-strong)",
                        color: "var(--text)",
                    }}
                    data-testid="rule-dialog"
                >
                    <DialogHeader>
                        <DialogTitle
                            className="mono text-sm uppercase tracking-[0.18em]"
                            style={{ color: "var(--accent)" }}
                        >
                            {editing?.id ? "edit rule" : "new rule"}
                        </DialogTitle>
                        <DialogDescription
                            className="mono text-[11px]"
                            style={{ color: "var(--text-dim)" }}
                        >
                            Route matching events to a channel. Lower priority =
                            evaluated first.
                        </DialogDescription>
                    </DialogHeader>
                    {editing !== null && (
                        <RuleForm
                            initial={editing?.id ? editing : null}
                            channels={channels}
                            submitting={saving}
                            onSubmit={save}
                            onCancel={() => setEditing(null)}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={deleting !== null}
                onOpenChange={(o) => !o && setDeleting(null)}
            >
                <AlertDialogContent
                    className="mono"
                    style={{
                        background: "var(--bg-elev)",
                        border: "1px solid var(--border-strong)",
                        color: "var(--text)",
                    }}
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle
                            className="mono text-sm uppercase tracking-[0.18em]"
                            style={{ color: "var(--danger)" }}
                        >
                            delete rule
                        </AlertDialogTitle>
                        <AlertDialogDescription
                            className="mono text-[11px]"
                            style={{ color: "var(--text-dim)" }}
                        >
                            {deleting?.name
                                ? `Delete rule '${deleting.name}'? This cannot be undone.`
                                : ""}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="ghost-btn">
                            cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={remove}
                            className="pill-btn"
                            style={{
                                background: "var(--danger)",
                                color: "#1a0610",
                                borderColor: "var(--danger)",
                            }}
                            data-testid="rule-delete-confirm"
                        >
                            delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </section>
    );
}
